import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  deviceInventoryStatusType,
  type default as DeviceInventoryStatusType,
} from './DeviceInventoryStatusType.js'
import { loggerId, type LoggerId } from './Logger.js'
import { organizationId, type OrganizationId } from './Organization.js'
import { projectId, type ProjectId } from './Project.js'

/** Identifier type for public.logger_history_ */
export type LoggerHistoryId = number & { __brand: 'LoggerHistoryId' }

/** Represents the table public.logger_history_ */
export default interface LoggerHistoryTable {
  /** Database type: pg_catalog.int4 */
  id: ColumnType<LoggerHistoryId, LoggerHistoryId | undefined, LoggerHistoryId>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.int4 */
  loggerId: ColumnType<LoggerId, LoggerId, LoggerId>

  /** Database type: pg_catalog.timestamptz */
  startedAt: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.timestamptz */
  endedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: public.device_inventory_status_type_ */
  inventoryStatus: ColumnType<
    DeviceInventoryStatusType | null,
    DeviceInventoryStatusType | null,
    DeviceInventoryStatusType | null
  >

  /** Database type: pg_catalog.int4 */
  organizationId: ColumnType<OrganizationId, OrganizationId, OrganizationId>

  /** Database type: pg_catalog.int4 */
  projectId: ColumnType<ProjectId, ProjectId, ProjectId>
}

export type LoggerHistory = Selectable<LoggerHistoryTable>

export type LoggerHistoryInitializer = Insertable<LoggerHistoryTable>

export type LoggerHistoryMutator = Updateable<LoggerHistoryTable>

export const loggerHistoryId =
  z.number() as unknown as z.Schema<LoggerHistoryId>

export const loggerHistory = z.object({
  id: loggerHistoryId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: z.string().nullable().optional(),
  loggerId: loggerId,
  startedAt: z.coerce.date(),
  endedAt: z.coerce.date().nullable().optional(),
  inventoryStatus: deviceInventoryStatusType.nullable().optional(),
  organizationId: organizationId,
  projectId: projectId,
}) as unknown as z.Schema<LoggerHistory>

export const loggerHistoryInitializer = z.object({
  id: loggerHistoryId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  loggerId: loggerId,
  startedAt: z.coerce.date(),
  endedAt: z.coerce.date().optional().nullable(),
  inventoryStatus: deviceInventoryStatusType.optional().nullable(),
  organizationId: organizationId,
  projectId: projectId,
}) as unknown as z.Schema<LoggerHistoryInitializer>

export const loggerHistoryMutator = z.object({
  id: loggerHistoryId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  loggerId: loggerId.optional(),
  startedAt: z.coerce.date().optional(),
  endedAt: z.coerce.date().optional().nullable(),
  inventoryStatus: deviceInventoryStatusType.optional().nullable(),
  organizationId: organizationId.optional(),
  projectId: projectId.optional(),
}) as unknown as z.Schema<LoggerHistoryMutator>
